import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Settings, Loader2, CheckCircle2, Mail, ArrowRight } from "lucide-react";
import { authFetch } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import AdminLoading from "../../components/admin/AdminLoading";
import ImageUploadField from "../../components/admin/ImageUploadField";
import { useSettings } from "../../context/SettingsContext";
import { ADMIN_FIELD, ADMIN_PANEL, BTN_PRIMARY } from "../../config/brand";
import { toast } from "sonner";

const emptyForm = {
  brand_name: "",
  brand_short: "",
  logo_url: "",
  logo_long_url: "",
  hero_image_url: "",
  hero_title: "",
  hero_subtitle: "",
  seo_description: "",
  centers_count: "5",
  contact_email: "",
  contact_phone: "",
  contact_address: "",
  social_facebook: "",
  social_instagram: "",
  social_linkedin: "",
  social_youtube: "",
  brevo_api_key: "",
  booking_center_step_enabled: false,
};

export default function AdminSettings() {
  const { refreshSettings } = useSettings();
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [brevoConfigured, setBrevoConfigured] = useState(false);
  const [brevoFromEnv, setBrevoFromEnv] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await authFetch("/api/admin/settings");
      if (res.ok) {
        const data = await res.json();
        setForm({
          brand_name: data.brand_name || "",
          brand_short: data.brand_short || "",
          logo_url: data.logo_url || "",
          logo_long_url: data.logo_long_url || "",
          hero_image_url: data.hero_image_url || "",
          hero_title: data.hero_title || "",
          hero_subtitle: data.hero_subtitle || "",
          seo_description: data.seo_description || "",
          centers_count: String(data.centers_count ?? 5),
          contact_email: data.contact_email || "",
          contact_phone: data.contact_phone || "",
          contact_address: data.contact_address || "",
          social_facebook: data.social_facebook || "",
          social_instagram: data.social_instagram || "",
          social_linkedin: data.social_linkedin || "",
          social_youtube: data.social_youtube || "",
          brevo_api_key: "",
          booking_center_step_enabled: data.booking_center_step_enabled === true,
        });
        setBrevoConfigured(Boolean(data.brevo_configured));
        setBrevoFromEnv(Boolean(data.brevo_from_env));
      } else {
        toast.error("Impossible de charger les paramètres");
      }
    } catch {
      toast.error("Erreur réseau");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const validate = () => {
    const next = {};
    if (!form.brand_name.trim()) next.brand_name = "Le nom de marque est requis";
    if (!form.brand_short.trim()) next.brand_short = "Les initiales sont requises";
    const count = parseInt(form.centers_count, 10);
    if (!Number.isFinite(count) || count < 1 || count > 50) {
      next.centers_count = "Indiquez un nombre entre 1 et 50";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const save = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    setSaved(false);
    try {
      const { brevo_api_key, ...rest } = form;
      const payload = {
        ...rest,
        centers_count: parseInt(form.centers_count, 10),
      };
      if (brevo_api_key.trim()) {
        payload.brevo_api_key = brevo_api_key.trim();
      }
      const res = await authFetch("/api/admin/settings", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Erreur lors de l'enregistrement");
      }
      const savedData = await res.json();
      setBrevoConfigured(Boolean(savedData.brevo_configured));
      setBrevoFromEnv(Boolean(savedData.brevo_from_env));
      await refreshSettings();
      toast.success("Paramètres enregistrés");
      setSaved(true);
      setForm((prev) => ({ ...prev, brevo_api_key: "" }));
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const replaceImage = async (field, url) => {
    updateField(field, url);
    try {
      const res = await authFetch("/api/admin/settings", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ [field]: url }),
      });
      if (!res.ok) {
        toast.error("Image importée — clique Enregistrer pour l'appliquer");
        return;
      }
      await refreshSettings();
      toast.success("Image remplacée");
    } catch {
      toast.error("Image importée — clique Enregistrer pour l'appliquer");
    }
  };

  if (loading) {
    return <AdminLoading message="Chargement des paramètres..." />;
  }

  return (
    <div className="max-w-2xl space-y-6">
      <AdminPageHeader
        icon={Settings}
        title="Paramètres du site"
        description="Marque, logos, contact et textes de la page d'accueil"
      />

      <form onSubmit={save} className="space-y-6">
        <Card className={ADMIN_PANEL}>
          <CardHeader>
            <CardTitle className="text-slate-900">Identité visuelle</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-700">Nom de marque *</Label>
                <Input
                  value={form.brand_name}
                  onChange={(e) => updateField("brand_name", e.target.value)}
                  placeholder="DigiRobot ou EduRobot"
                  className={`${ADMIN_FIELD} ${errors.brand_name ? "border-red-500" : ""}`}
                  aria-invalid={!!errors.brand_name}
                />
                {errors.brand_name && (
                  <p className="text-red-400 text-xs mt-1">{errors.brand_name}</p>
                )}
              </div>
              <div>
                <Label className="text-slate-700">Initiales (logo) *</Label>
                <Input
                  value={form.brand_short}
                  onChange={(e) => updateField("brand_short", e.target.value.toUpperCase().slice(0, 3))}
                  placeholder="DR"
                  className={`${ADMIN_FIELD} ${errors.brand_short ? "border-red-500" : ""}`}
                  maxLength={3}
                  aria-invalid={!!errors.brand_short}
                />
                {errors.brand_short && (
                  <p className="text-red-400 text-xs mt-1">{errors.brand_short}</p>
                )}
              </div>
            </div>

            <ImageUploadField
              label="Logo"
              hint="Navbar, favicon. PNG, JPG ou WebP — 2 Mo max."
              kind="logo"
              value={form.logo_url}
              onChange={(url) => updateField("logo_url", url)}
              onUploaded={(url) => replaceImage("logo_url", url)}
              previewAlt="Aperçu logo"
              previewClassName="h-16 w-auto max-w-[160px] object-contain rounded-lg border border-slate-200 bg-white p-2"
            />

            <ImageUploadField
              label="Logo long"
              hint="Menu, footer et e-mails. PNG, JPG ou WebP — 2 Mo max."
              kind="logo_long"
              value={form.logo_long_url}
              onChange={(url) => updateField("logo_long_url", url)}
              onUploaded={(url) => replaceImage("logo_long_url", url)}
              previewAlt="Aperçu logo long"
              previewClassName="h-12 w-auto max-w-[240px] object-contain rounded-lg border border-slate-200 bg-white p-2"
            />

            <ImageUploadField
              label="Image hero"
              hint="Grande image d'accueil. PNG, JPG ou WebP — 5 Mo max."
              kind="hero"
              value={form.hero_image_url}
              onChange={(url) => updateField("hero_image_url", url)}
              onUploaded={(url) => replaceImage("hero_image_url", url)}
              previewAlt="Aperçu hero"
              previewClassName="h-32 w-full max-w-xs object-cover rounded-lg border border-slate-200"
            />

            <div>
              <Label className="text-slate-700">Titre hero</Label>
              <Input
                value={form.hero_title}
                onChange={(e) => updateField("hero_title", e.target.value)}
                className={ADMIN_FIELD}
              />
            </div>

            <div>
              <Label className="text-slate-700">Sous-titre hero</Label>
              <Textarea
                value={form.hero_subtitle}
                onChange={(e) => updateField("hero_subtitle", e.target.value)}
                className={ADMIN_FIELD}
                rows={3}
              />
            </div>

            <div>
              <Label className="text-slate-700">Description SEO</Label>
              <Textarea
                value={form.seo_description}
                onChange={(e) => updateField("seo_description", e.target.value)}
                placeholder="Meta description (onglet, Open Graph…)"
                className={ADMIN_FIELD}
                rows={2}
              />
            </div>

            <div>
              <Label className="text-slate-700">Nombre de centres</Label>
              <Input
                type="number"
                min={1}
                max={50}
                value={form.centers_count}
                onChange={(e) => updateField("centers_count", e.target.value)}
                className={`${ADMIN_FIELD} w-32 ${errors.centers_count ? "border-red-500" : ""}`}
                aria-invalid={!!errors.centers_count}
              />
              {errors.centers_count && (
                <p className="text-red-400 text-xs mt-1">{errors.centers_count}</p>
              )}
              <p className="text-slate-500 text-xs mt-1">Affiché dans les statistiques de la page d&apos;accueil.</p>
            </div>

            <label className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50/70 px-4 py-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.booking_center_step_enabled === true}
                onChange={(e) => updateField("booking_center_step_enabled", e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-slate-300 text-[#3a00e0] focus:ring-[#3a00e0]"
              />
              <span>
                <span className="block text-slate-800 font-medium text-sm">Choix du centre à la réservation</span>
                <span className="block text-slate-500 text-xs mt-0.5">
                  Désactivé : le tunnel passe de la formule au créneau. Les créneaux restent rattachés à un centre.
                </span>
              </span>
            </label>
          </CardContent>
        </Card>

        <Card className={ADMIN_PANEL}>
          <CardHeader>
            <CardTitle className="text-slate-900">Contact & réseaux</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div>
              <Label className="text-slate-700">E-mail de contact</Label>
              <Input
                type="email"
                value={form.contact_email}
                onChange={(e) => updateField("contact_email", e.target.value)}
                placeholder="contact@digirobot.fr"
                className={ADMIN_FIELD}
              />
            </div>
            <div>
              <Label className="text-slate-700">Téléphone</Label>
              <Input
                value={form.contact_phone}
                onChange={(e) => updateField("contact_phone", e.target.value)}
                placeholder="06 98 85 49 48"
                className={ADMIN_FIELD}
              />
            </div>
            <div>
              <Label className="text-slate-700">Adresse / zones</Label>
              <Input
                value={form.contact_address}
                onChange={(e) => updateField("contact_address", e.target.value)}
                placeholder="Nice, Cannes, Monaco…"
                className={ADMIN_FIELD}
              />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-700">Facebook</Label>
                <Input
                  value={form.social_facebook}
                  onChange={(e) => updateField("social_facebook", e.target.value)}
                  placeholder="https://facebook.com/..."
                  className={ADMIN_FIELD}
                />
              </div>
              <div>
                <Label className="text-slate-700">Instagram</Label>
                <Input
                  value={form.social_instagram}
                  onChange={(e) => updateField("social_instagram", e.target.value)}
                  placeholder="https://instagram.com/..."
                  className={ADMIN_FIELD}
                />
              </div>
              <div>
                <Label className="text-slate-700">LinkedIn</Label>
                <Input
                  value={form.social_linkedin}
                  onChange={(e) => updateField("social_linkedin", e.target.value)}
                  placeholder="https://linkedin.com/..."
                  className={ADMIN_FIELD}
                />
              </div>
              <div>
                <Label className="text-slate-700">YouTube</Label>
                <Input
                  value={form.social_youtube}
                  onChange={(e) => updateField("social_youtube", e.target.value)}
                  placeholder="https://youtube.com/..."
                  className={ADMIN_FIELD}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={ADMIN_PANEL}>
          <CardHeader>
            <CardTitle className="text-slate-900 flex items-center gap-2">
              <Mail className="h-5 w-5 text-[#3a00e0]" />
              E-mails automatiques (Brevo)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-slate-500">
              Collez une clé API Brevo pour activer les e-mails : confirmation parent,
              notification admin (contacts, leads, RDV, liste d&apos;attente). Une clé déjà
              présente sur le serveur a toujours la priorité. Le destinataire admin est
              l&apos;e-mail de contact des Paramètres.
            </p>
            <Link
              to="/admin/emails"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-[#3a00e0] hover:underline"
            >
              Voir et modifier les modèles d&apos;e-mails
              <ArrowRight className="h-4 w-4" />
            </Link>
            <div
              className={`text-sm rounded-lg px-3 py-2 ${
                brevoConfigured
                  ? "bg-green-50 text-green-700"
                  : "bg-slate-50 text-slate-500"
              }`}
            >
              {brevoFromEnv
                ? "E-mails actifs (clé serveur)."
                : brevoConfigured
                  ? "E-mails actifs (clé enregistrée dans les paramètres)."
                  : "E-mails inactifs — aucune clé pour le moment."}
            </div>
            <div>
              <Label className="text-slate-700">Clé Brevo / activer les e-mails</Label>
              <Input
                type="password"
                autoComplete="new-password"
                value={form.brevo_api_key}
                onChange={(e) => updateField("brevo_api_key", e.target.value)}
                placeholder={brevoConfigured ? "Laisser vide pour conserver la clé actuelle" : "xkeysib-…"}
                className={ADMIN_FIELD}
              />
              <p className="text-slate-500 text-xs mt-1">
                Compte Brevo → SMTP &amp; API → clé API. La clé n’est jamais réaffichée après
                enregistrement.
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="flex items-center gap-3">
          <Button
            type="submit"
            disabled={saving}
            className={`${BTN_PRIMARY} min-w-[140px]`}
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Enregistrement...
              </>
            ) : (
              "Enregistrer"
            )}
          </Button>
          {saved && (
            <span className="flex items-center gap-1.5 text-sm text-green-600">
              <CheckCircle2 className="h-4 w-4" />
              Enregistré
            </span>
          )}
        </div>
      </form>
    </div>
  );
}
